import type { CustomExperimentSpec } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import {
  createFormulaLegend,
  createSvgEl,
  createZoomViewport,
  type FormulaLegendEntry,
  type ViewportView,
} from '@learnforge/demo-render';
import {
  createSliderRow,
  createToggleRow,
  createDropdownRow,
  type SliderRowHandle,
} from '@learnforge/demo-kit';
import { createAxes } from '../render/axes';
import { createCurve, type CurveHandle } from '../render/curve';
import { createWaveAnnotations } from '../render/wave-annotations';
import {
  evaluateWave,
  rForm,
  formatWaveFormula,
  formatRFormFormula,
  waveAnnotations,
  type WaveParams,
} from '../trig/waves';
import { formatRadian } from '../trig/angles';
import { createActionBar } from '../action-bar';
import type { TrigContext } from '../types';
import type { TrigKey } from '../i18n';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_PARAMS: WaveParams = { fn: 'sin', A: 1, B: 1, C: 0, D: 0 };
const DEFAULT_R = { a: 1, b: 1 };
export const MAX_FUNCTIONS = 6;
const BASE_VIEW = { x: -7, y: -3, width: 14, height: 6 };
const BASE_X_DOMAIN: [number, number] = [BASE_VIEW.x, BASE_VIEW.x + BASE_VIEW.width];
const BASE_SAMPLES = 480;

const EYE_SVG =
  '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">' +
  '<path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12z" fill="none" stroke="currentColor" stroke-width="1.8"/>' +
  '<circle cx="12" cy="12" r="3" fill="currentColor"/>' +
  '<line class="tg-eye-slash" x1="3" y1="21" x2="21" y2="3" stroke="currentColor" stroke-width="1.8"/>' +
  '</svg>';

function fmt3(v: number): string {
  return v.toFixed(3);
}

// ---------------------------------------------------------------------------
// State — model only (no DOM refs)
// ---------------------------------------------------------------------------

export type Slot = 1 | 2 | 3 | 4 | 5 | 6;

export interface WaveFn {
  id: number;
  /** Colour slot (--series-N). Fixed for the function's lifetime. */
  slot: Slot;
  visible: boolean;
  params: WaveParams;
}

export interface State {
  functions: WaveFn[];
  activeId: number;
  nextId: number;
  showParams: boolean;
  showBase: boolean;
  rOn: boolean;
  rA: number;
  rB: number;
}

export function initialState(): State {
  return {
    functions: [{ id: 1, slot: 1, visible: true, params: { ...DEFAULT_PARAMS } }],
    activeId: 1,
    nextId: 2,
    showParams: true,
    showBase: true,
    rOn: false,
    rA: DEFAULT_R.a,
    rB: DEFAULT_R.b,
  };
}

export function activeFn(s: State): WaveFn {
  return s.functions.find((f) => f.id === s.activeId) ?? s.functions[0]!;
}

function freeSlot(functions: readonly WaveFn[]): Slot | null {
  for (let slot = 1; slot <= MAX_FUNCTIONS; slot++) {
    if (!functions.some((f) => f.slot === slot)) return slot as Slot;
  }
  return null;
}

/** New function = copy of the active one, lowest free slot, becomes active. No-op at the cap. */
export function addFunction(s: State): State {
  const slot = freeSlot(s.functions);
  if (slot === null) return s;
  const fn: WaveFn = { id: s.nextId, slot, visible: true, params: { ...activeFn(s).params } };
  return { ...s, functions: [...s.functions, fn], activeId: fn.id, nextId: s.nextId + 1 };
}

/** Never removes the last function; removing the active one activates the first remaining. */
export function removeFunction(s: State, id: number): State {
  if (s.functions.length <= 1) return s;
  const functions = s.functions.filter((f) => f.id !== id);
  const activeId = s.activeId === id ? functions[0]!.id : s.activeId;
  return { ...s, functions, activeId };
}

export function toggleVisible(s: State, id: number): State {
  return {
    ...s,
    functions: s.functions.map((f) => (f.id === id ? { ...f, visible: !f.visible } : f)),
  };
}

export function updateActiveParams(s: State, patch: Partial<WaveParams>): State {
  return {
    ...s,
    functions: s.functions.map((f) =>
      f.id === s.activeId ? { ...f, params: { ...f.params, ...patch } } : f,
    ),
  };
}

// ---------------------------------------------------------------------------
// Spec factory
// ---------------------------------------------------------------------------

interface CardRefs {
  root: HTMLDivElement;
  formula: HTMLSpanElement;
  eye: HTMLButtonElement;
  remove: HTMLButtonElement;
}

function buildSpec(): CustomExperimentSpec<State, TrigKey, TrigContext> {
  return {
    labelKey: 'experiment.wave-studio.label' as TrigKey,
    demoId: 'trigonometry',
    advancedKey: 'wave-studio',

    initialState,

    createRender(ctx, host, controls) {
      const i18nUnsubs: Array<() => void> = [];
      function trackSubscribe(callback: () => void): void {
        i18nUnsubs.push(ctx.i18n.subscribe(callback));
      }

      // ---- SVG stage ----
      const svg = createSvgEl('svg', {
        viewBox: `${BASE_VIEW.x} ${BASE_VIEW.y} ${BASE_VIEW.width} ${BASE_VIEW.height}`,
        preserveAspectRatio: 'xMidYMid meet',
        class: 'tg-svg',
        'font-size': '0.15',
      });
      host.appendChild(svg);
      const world = createSvgEl('g', { transform: 'scale(1 -1)' });
      svg.appendChild(world);

      // ---- Axes (x in π, y in whole units at the base view) ----
      const axes = createAxes({
        xDomain: BASE_X_DOMAIN,
        yDomain: [-3, 3],
        xTickMode: 'pi',
        targetTicks: { x: 10, y: 6 },
        i18n: ctx.i18n,
      });
      world.appendChild(axes.group);

      // ---- Curves: base ghost, then one per function in curveLayer, then R-form ----
      const baseCurve = createCurve(world, {
        fn: Math.sin,
        xDomain: BASE_X_DOMAIN,
        samples: BASE_SAMPLES,
        className: 'tg-curve-ghost',
      });
      const annotations = createWaveAnnotations(world); // z-order: above base, below functions
      const curveLayer = createSvgEl('g', { class: 'tg-curves' });
      world.appendChild(curveLayer);
      const curves = new Map<number, CurveHandle>();
      const rCurve = createCurve(world, {
        fn: () => 0,
        xDomain: BASE_X_DOMAIN,
        samples: BASE_SAMPLES,
        className: 'tg-curve-r',
      });
      rCurve.element.style.display = 'none';

      const legend = createFormulaLegend(host);

      // ---- Viewport ----
      let view: ViewportView | null = null;
      const samplesFor = (v: ViewportView): number =>
        Math.min(
          2400,
          Math.max(240, Math.round((BASE_SAMPLES * v.viewBox.width) / BASE_VIEW.width)),
        );
      const clipFor = (v: ViewportView | null): [number, number] =>
        v ? [v.yDomain[0] - 1, v.yDomain[1] + 1] : [-4, 4];
      const xDomainFor = (v: ViewportView | null): [number, number] =>
        v ? v.xDomain : BASE_X_DOMAIN;

      let latest: State | null = null;
      function refreshAnnotations(state: State): void {
        const active = activeFn(state);
        const show = state.showParams && active.visible;
        annotations.update(show ? waveAnnotations(active.params) : null, {
          xDomain: xDomainFor(view),
          color: `var(--series-${active.slot})`,
          chromeScale: view ? 1 / view.zoom : 1,
        });
      }

      function layoutView(): void {
        if (!view) return;
        axes.update({ xDomain: view.xDomain, yDomain: view.yDomain, chromeScale: 1 / view.zoom });
        const samples = samplesFor(view);
        baseCurve.setView({ xDomain: view.xDomain, samples });
        for (const c of curves.values())
          c.setView({ xDomain: view.xDomain, samples, clipAtY: clipFor(view) });
        rCurve.setView({ xDomain: view.xDomain, samples });
        if (latest) refreshAnnotations(latest);
      }

      const viewport = createZoomViewport(svg, {
        base: BASE_VIEW,
        yUp: true,
        fontSize: 0.15,
        onChange: (v) => {
          view = v;
          layoutView();
        },
      });

      // ---- Panel: Functions section ----
      const fnSection = document.createElement('div');
      fnSection.className = 'tg-panel-section tg-fn-section';
      const fnTitle = document.createElement('div');
      fnTitle.className = 'tg-panel-label';
      fnTitle.textContent = ctx.i18n.t('ws.functions');
      trackSubscribe(() => {
        fnTitle.textContent = ctx.i18n.t('ws.functions');
      });
      const cardList = document.createElement('div');
      cardList.className = 'tg-fn-list';
      const addBtn = document.createElement('button');
      addBtn.type = 'button';
      addBtn.className = 'tg-fn-add';
      addBtn.textContent = `+ ${ctx.i18n.t('ws.add')}`;
      trackSubscribe(() => {
        addBtn.textContent = `+ ${ctx.i18n.t('ws.add')}`;
      });
      addBtn.addEventListener('click', () => controls.setState(addFunction));
      fnSection.append(fnTitle, cardList, addBtn);
      ctx.panel.appendChild(fnSection);

      const cards = new Map<number, CardRefs>();
      trackSubscribe(() => {
        for (const card of cards.values()) {
          card.eye.setAttribute('aria-label', ctx.i18n.t('ws.visible'));
          card.remove.setAttribute('aria-label', ctx.i18n.t('ws.remove'));
        }
      });

      function buildCard(f: WaveFn): CardRefs {
        const root = document.createElement('div');
        root.className = 'tg-fn-card';
        root.dataset.id = String(f.id);
        root.style.setProperty('--card-color', `var(--series-${f.slot})`);

        const select = document.createElement('button');
        select.type = 'button';
        select.className = 'tg-fn-select';
        const key = document.createElement('span');
        key.className = 'tg-fn-key';
        key.style.setProperty('--legend-color', `var(--series-${f.slot})`);
        const formula = document.createElement('span');
        formula.className = 'tg-fn-formula';
        select.append(key, formula);
        select.addEventListener('click', () =>
          controls.setState((s) => ({ ...s, activeId: f.id })),
        );

        const eye = document.createElement('button');
        eye.type = 'button';
        eye.className = 'tg-fn-eye';
        eye.innerHTML = EYE_SVG;
        eye.setAttribute('aria-label', ctx.i18n.t('ws.visible'));
        eye.addEventListener('click', () => controls.setState((s) => toggleVisible(s, f.id)));

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'tg-fn-remove';
        remove.textContent = '✕';
        remove.setAttribute('aria-label', ctx.i18n.t('ws.remove'));
        remove.addEventListener('click', () => controls.setState((s) => removeFunction(s, f.id)));

        root.append(select, eye, remove);
        return { root, formula, eye, remove };
      }

      // ---- Panel: active-function editor ----
      const fnOptions: Array<{ value: WaveParams['fn']; labelKey: TrigKey }> = [
        { value: 'sin', labelKey: 'ws.fn.sin' },
        { value: 'cos', labelKey: 'ws.fn.cos' },
        { value: 'tan', labelKey: 'ws.fn.tan' },
        { value: 'csc', labelKey: 'ws.fn.csc' },
        { value: 'sec', labelKey: 'ws.fn.sec' },
        { value: 'cot', labelKey: 'ws.fn.cot' },
      ];
      const fnDropdown = createDropdownRow({
        id: 'ws-fn',
        i18n: ctx.i18n,
        options: fnOptions,
        value: DEFAULT_PARAMS.fn,
        labelKey: 'ws.fn',
        onChange: (v) => controls.setState((s) => updateActiveParams(s, { fn: v })),
      });
      ctx.panel.appendChild(fnDropdown.element);

      function paramSlider(
        id: string,
        labelKey: TrigKey,
        key: 'A' | 'B' | 'C' | 'D',
        min: number,
        max: number,
      ): SliderRowHandle {
        const handle = createSliderRow({
          id,
          labelKey,
          i18n: ctx.i18n,
          min,
          max,
          step: 0.05,
          value: DEFAULT_PARAMS[key],
          format: fmt3,
          onChange: (v) =>
            controls.setState((s) => {
              // A computed key `{ [key]: v }` widens to an index signature, which is not
              // assignable to Partial<WaveParams>; assign through a typed patch instead.
              const patch: Partial<WaveParams> = {};
              patch[key] = v;
              return updateActiveParams(s, patch);
            }),
        });
        ctx.panel.appendChild(handle.element);
        return handle;
      }
      const sliderA = paramSlider('ws-A', 'ws.amplitude', 'A', -3, 3);
      const sliderB = paramSlider('ws-B', 'ws.frequency', 'B', 0.25, 3);
      const sliderC = paramSlider('ws-C', 'ws.phase', 'C', -Math.PI, Math.PI);
      const sliderD = paramSlider('ws-D', 'ws.shift', 'D', -2.5, 2.5);

      const showParamsToggle = createToggleRow({
        labelKey: 'ws.showParams',
        i18n: ctx.i18n,
        checked: true,
        onChange: (v) => controls.setState((s) => ({ ...s, showParams: v })),
      });
      ctx.panel.appendChild(showParamsToggle.element);

      // ---- Panel: R-form ----
      const rToggle = createToggleRow({
        labelKey: 'ws.rform.toggle',
        i18n: ctx.i18n,
        checked: false,
        onChange: (v) => controls.setState((s) => ({ ...s, rOn: v })),
      });
      ctx.panel.appendChild(rToggle.element);

      const rFormSection = document.createElement('div');
      rFormSection.className = 'tg-panel-section';
      rFormSection.style.display = 'none';

      const sliderRa = createSliderRow({
        id: 'ws-ra',
        labelKey: 'ws.rform.a',
        i18n: ctx.i18n,
        min: -2,
        max: 2,
        step: 0.05,
        value: DEFAULT_R.a,
        format: fmt3,
        onChange: (v) => controls.setState((s) => ({ ...s, rA: v })),
      });
      rFormSection.appendChild(sliderRa.element);
      const sliderRb = createSliderRow({
        id: 'ws-rb',
        labelKey: 'ws.rform.b',
        i18n: ctx.i18n,
        min: -2,
        max: 2,
        step: 0.05,
        value: DEFAULT_R.b,
        format: fmt3,
        onChange: (v) => controls.setState((s) => ({ ...s, rB: v })),
      });
      rFormSection.appendChild(sliderRb.element);

      function readoutRow(labelKey: TrigKey): HTMLSpanElement {
        const row = document.createElement('div');
        row.className = 'tg-readout-row';
        const label = document.createElement('span');
        label.className = 'tg-panel-label';
        label.textContent = ctx.i18n.t(labelKey);
        trackSubscribe(() => {
          label.textContent = ctx.i18n.t(labelKey);
        });
        const value = document.createElement('span');
        value.className = 'tg-ratio-value';
        row.append(label, value);
        rFormSection.appendChild(row);
        return value;
      }
      const rValueEl = readoutRow('ws.rform.r');
      const alphaValueEl = readoutRow('ws.rform.alpha');
      ctx.panel.appendChild(rFormSection);

      // ---- Action bar ----
      const actionBarHandle = createActionBar(ctx.actionBar, {
        buttons: [
          { id: 'reset', labelKey: 'ws.reset', onClick: () => controls.setState(initialState()) },
          {
            id: 'base',
            labelKey: 'ws.base',
            onClick: () => controls.setState((s) => ({ ...s, showBase: !s.showBase })),
          },
          { id: 'view', labelKey: 'view.reset', onClick: () => viewport.reset() },
        ],
        i18n: ctx.i18n,
      });

      // ---- update — reconcile view from state ----
      function update(state: State): void {
        latest = state;
        const active = activeFn(state);
        const ids = new Set(state.functions.map((f) => f.id));

        // Cards (keyed by id; appendChild keeps DOM order = functions order).
        for (const [id, card] of cards) {
          if (!ids.has(id)) {
            card.root.remove();
            cards.delete(id);
          }
        }
        for (const f of state.functions) {
          let card = cards.get(f.id);
          if (!card) {
            card = buildCard(f);
            cards.set(f.id, card);
          }
          cardList.appendChild(card.root);
          card.formula.textContent = formatWaveFormula(f.params);
          card.root.dataset.active = f.id === state.activeId ? 'true' : 'false';
          card.eye.setAttribute('aria-pressed', f.visible ? 'true' : 'false');
          card.remove.disabled = state.functions.length <= 1;
        }
        addBtn.disabled = state.functions.length >= MAX_FUNCTIONS;

        // Editor follows the active function (setValue skips a focused text field).
        fnDropdown.setValue(active.params.fn);
        sliderA.setValue(active.params.A);
        sliderB.setValue(active.params.B);
        sliderC.setValue(active.params.C);
        sliderD.setValue(active.params.D);
        showParamsToggle.setChecked(state.showParams);
        rToggle.setChecked(state.rOn);
        sliderRa.setValue(state.rA);
        sliderRb.setValue(state.rB);

        // Curves (keyed by id).
        for (const [id, c] of curves) {
          if (!ids.has(id)) {
            c.destroy();
            curves.delete(id);
          }
        }
        for (const f of state.functions) {
          const params = f.params;
          let c = curves.get(f.id);
          if (!c) {
            c = createCurve(curveLayer, {
              fn: (x) => evaluateWave(params, x),
              xDomain: xDomainFor(view),
              samples: view ? samplesFor(view) : BASE_SAMPLES,
              className: `tg-series-${f.slot}`,
              clipAtY: clipFor(view),
            });
            curves.set(f.id, c);
          } else {
            c.setFn((x) => evaluateWave(params, x));
          }
          c.element.style.display = f.visible ? '' : 'none';
          c.element.classList.toggle('tg-curve-active', f.id === state.activeId);
        }
        const activeCurve = curves.get(state.activeId);
        if (activeCurve) curveLayer.appendChild(activeCurve.element); // active on top

        baseCurve.element.style.display = state.showBase ? '' : 'none';

        // R-form
        rFormSection.style.display = state.rOn ? '' : 'none';
        if (state.rOn) {
          const { R, alpha } = rForm(state.rA, state.rB);
          rCurve.setFn((x) => R * Math.sin(x + alpha));
          rCurve.element.style.display = '';
          rValueEl.textContent = R.toFixed(3);
          alphaValueEl.textContent = formatRadian(alpha);
        } else {
          rCurve.element.style.display = 'none';
        }

        // Legend: visible functions in slot order, then R-form, then the base curve.
        const entries: FormulaLegendEntry[] = state.functions
          .filter((f) => f.visible)
          .sort((a, b) => a.slot - b.slot)
          .map((f) => ({
            id: `function-${f.id}`,
            formula: formatWaveFormula(f.params),
            color: `var(--series-${f.slot})`,
          }));
        if (state.rOn) {
          entries.push({
            id: 'r-form',
            formula: formatRFormFormula(state.rA, state.rB),
            color: 'var(--red)',
            lineStyle: 'dashed',
          });
        }
        if (state.showBase)
          entries.push({
            id: 'base',
            formula: 'y = sin(x)',
            color: 'var(--ink-soft)',
            lineStyle: 'dashed',
          });
        legend.setEntries(entries);
        refreshAnnotations(state);
      }

      function dispose(): void {
        for (const c of curves.values()) c.destroy();
        curves.clear();
        baseCurve.destroy();
        rCurve.destroy();
        annotations.destroy();
        axes.destroy();
        legend.destroy();
        viewport.dispose();
        actionBarHandle.destroy();
        for (const off of i18nUnsubs) off();
        i18nUnsubs.length = 0;
      }

      return { update, dispose };
    },

    applyShotState(state, shotId): State {
      if (shotId !== 'wave-studio') return state;
      // Two functions so the reel shows the legend and the annotations.
      const f1: WaveFn = { id: 1, slot: 1, visible: true, params: { ...DEFAULT_PARAMS } };
      const f2: WaveFn = {
        id: 2,
        slot: 2,
        visible: true,
        params: { fn: 'cos', A: 1.5, B: 2, C: 0.5, D: 0.5 },
      };
      return { ...state, functions: [f1, f2], activeId: 2, nextId: 3, showParams: true };
    },
  };
}

export const experiment = defineExperiment(buildSpec());
export const mountWaveStudio = experiment.mount.bind(experiment);
